import type { ReschedulingReason, ShuffleInspector } from '../types';
import type { ActivityDTO } from 'src/types/dto/activity.dto';
import type { AppointmentDTO } from 'src/types/dto/appointment.dto';
import type { ShuffleFilterDraft } from '../build-reschedule-request-body';

import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import { useGetBusinessUnitsForSelection } from 'src/actions/business-unit';
import { Iconify } from 'src/components/iconify';
import { RestrictedDatePicker } from 'src/components/restricted-date-picker';
import { VirtualizedAutocompleteMultiple } from 'src/components/virtualized-autocomplete';
import { useListForFilter } from 'src/hooks/use-list-for-filter';
import { InspectorDTO } from 'src/types/dto/inspector.dto';
import { endpoints } from 'src/utils/axios';
import { getCompetencyName } from 'src/utils/competency';

import { getCurrentActivityInspector } from '../get-current-activity-inspector';
import { getCurrentNonBlockerAppointmentDates } from '../get-current-non-blocker-appointment-dates';
import { formatSelectedDateRangeLabel } from '../group-selected-dates';
import {
  stepHeaderPillLabelSx,
} from '../step-header/step-header-pill-styles';

const FILTER_PILL_HEIGHT = 34;
const FILTER_PILL_PADDING_X = 1.5;

const getFilterAutocompleteTextFieldProps = (minWidth = 160) => ({
  size: 'small' as const,
  sx: (theme: Theme) => ({
    minWidth,
    '& .MuiOutlinedInput-root': {
      minHeight: FILTER_PILL_HEIGHT,
      px: FILTER_PILL_PADDING_X,
      borderRadius: 999,
      bgcolor: theme.vars.palette.background.paper,
      color: theme.vars.palette.text.primary,
      '& fieldset': {
        borderColor: theme.vars.palette.action.disabledBackground,
        borderWidth: '1px',
      },
      '& .MuiChip-root': {
        maxHeight: 22,
      },
    },
    '& .MuiOutlinedInput-input, & .MuiAutocomplete-input': {
      px: '0 !important',
    },
    '& .MuiInputLabel-root': {
      color: `${theme.vars.palette.text.primary} !important`,
      fontWeight: 700,
      fontSize: 13,
    },
  }),
});

type FilterPillState = {
  active?: boolean;
  open?: boolean;
};

const filterPillSx = ({ active = false, open = false }: FilterPillState = {}): SxProps<Theme> => (
  theme
) => {
  const isSelected = active || open;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    height: FILTER_PILL_HEIGHT,
    minWidth: FILTER_PILL_HEIGHT,
    px: FILTER_PILL_PADDING_X,
    py: 0,
    borderRadius: 999,
    border: `${isSelected ? '1.5px solid' : '1px solid'}`,
    borderColor: isSelected
      ? theme.vars.palette.text.primary
      : theme.vars.palette.action.disabledBackground,
    bgcolor: theme.vars.palette.background.paper,
    color: theme.vars.palette.text.primary,
    flexShrink: 0,
    cursor: 'pointer',
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      bgcolor: theme.vars.palette.background.paper,
      boxShadow: 'none',
      borderColor: isSelected
        ? theme.vars.palette.text.primary
        : theme.vars.palette.action.disabledBackground,
    },
  };
};

interface ShuffleFilterAutocompleteMultipleProps {
  label: string;
  options: { key: number; label: string }[];
  selectedValues: number[];
  onChange: (values: number[]) => void;
  disabled?: boolean;
  minWidth?: number;
  limitTags?: number;
  hideSelectionWhenClosed?: boolean;
  pinSelectedOnTop?: boolean;
  serverSideSearch?: boolean;
  onEndReached?: () => void;
  onTextChange?: (text: string | null) => void;
  placeholder?: string;
}

const ShuffleFilterAutocompleteMultiple = memo(
  ({
    label,
    options,
    selectedValues,
    onChange,
    disabled = false,
    minWidth = 200,
    limitTags = -1,
    hideSelectionWhenClosed = false,
    pinSelectedOnTop = false,
    serverSideSearch = false,
    onEndReached,
    onTextChange,
    placeholder,
  }: ShuffleFilterAutocompleteMultipleProps) => (
      <VirtualizedAutocompleteMultiple
        value={selectedValues}
        onChange={(value) => onChange(((value as number[] | null | undefined) ?? []) as number[])}
        options={options}
        label={label}
        disabled={disabled}
        limitTags={limitTags}
        hideSelectionWhenClosed={hideSelectionWhenClosed}
        pinSelectedOnTop={pinSelectedOnTop}
        serverSideSearch={serverSideSearch}
        disableCloseOnSelect
        onEndReached={onEndReached}
        onTextChange={onTextChange}
        textFieldProps={{
          ...getFilterAutocompleteTextFieldProps(minWidth),
          placeholder,
          InputLabelProps: {
            sx: {
              color: 'text.primary',
              fontWeight: 700,
              fontSize: 13,
            },
          },
        }}
      />
  )
);

ShuffleFilterAutocompleteMultiple.displayName = 'ShuffleFilterAutocompleteMultiple';

interface Step2FiltersProps {
  activityId: number;
  projectId: number;
  currentActivity: ActivityDTO;
  selectedReason: ReschedulingReason;
  appointment?: AppointmentDTO;
  filters: ShuffleFilterDraft;
  onFiltersChange: (patch: Partial<ShuffleFilterDraft>) => void;
  onReset: () => void;
  onWizardReset?: () => void;
  showWizardReset?: boolean;
  showFilterReset?: boolean;
  onStart: (selectedInspectors: ShuffleInspector[]) => void;
  isProgressRunning?: boolean;
  optionsCount?: number;
}

export const Step2Filters = memo(
  ({
    activityId,
    projectId,
    currentActivity,
    selectedReason,
    appointment,
    filters,
    onFiltersChange,
    onReset,
    onWizardReset,
    showWizardReset = false,
    showFilterReset = true,
    onStart,
    isProgressRunning = false,
    optionsCount,
  }: Step2FiltersProps) => {
    const { t } = useTranslation(['shuffle', 'activities']);
    const personFilterLabel = t('allocation.details.activity.inspector', { ns: 'activities' });
    const dateButtonRef = useRef<HTMLButtonElement>(null);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    const {
      isPreferred,
      inspectorType,
      selectedInspectors,
      selectedCompetencyIds,
      selectedBusinessUnitIds,
      selectedDate,
    } = filters;

    const selectedResourceIds = useMemo(
      () => selectedInspectors.map((inspector) => inspector.id),
      [selectedInspectors]
    );

    const currentActivityInspector = useMemo(
      () => getCurrentActivityInspector(currentActivity, appointment),
      [appointment, currentActivity]
    );
    const shouldAutoSelectCurrentInspector = selectedReason === 'customer_cancelled';
    const shouldFixAppointmentDate = selectedReason === 'person_cancelled';

    const fixedAppointmentDates = useMemo(
      () => getCurrentNonBlockerAppointmentDates(currentActivity, appointment),
      [appointment, currentActivity]
    );

    useEffect(() => {
      if (!shouldFixAppointmentDate || fixedAppointmentDates.length === 0) {
        return;
      }

      const fixedDate = fixedAppointmentDates[0];

      if (selectedDate?.isSame(fixedDate, 'day')) {
        return;
      }

      onFiltersChange({ selectedDate: fixedDate });
    }, [
      fixedAppointmentDates,
      onFiltersChange,
      selectedDate,
      shouldFixAppointmentDate,
    ]);

    const {
      businessUnitOptions,
      mutateBusinessUnitOptions,
    } = useGetBusinessUnitsForSelection();

    useEffect(() => {
      mutateBusinessUnitOptions();
    }, [activityId, mutateBusinessUnitOptions]);

    useEffect(() => {
      if (!shouldAutoSelectCurrentInspector || !currentActivityInspector) {
        return;
      }

      if (selectedResourceIds.includes(currentActivityInspector.id)) {
        return;
      }

      onFiltersChange({ selectedInspectors: [currentActivityInspector] });
    }, [
      currentActivityInspector,
      onFiltersChange,
      selectedResourceIds,
      shouldAutoSelectCurrentInspector,
    ]);

    const competencyOptions = useMemo(
      () =>
        currentActivity.activityCompetencies?.map((activityCompetency) => ({
          key: activityCompetency.competency.id,
          label: getCompetencyName(activityCompetency.competency),
        })) ?? [],
      [currentActivity.activityCompetencies]
    );

    const businessUnitOptionsList = useMemo(
      () => businessUnitOptions.map((unit) => ({ key: unit.id, label: unit.name })),
      [businessUnitOptions]
    );

    const accreditationBodyIds = useMemo(
      () =>
        currentActivity.accreditationBodies
          ?.map((accreditationBody) => accreditationBody.id)
          .filter((id): id is number => typeof id === 'number') ?? [],
      [currentActivity.accreditationBodies]
    );

    const resourceAdditionalParams = useMemo(
      () => ({
        ...(competencyOptions.length > 0
          ? { competenciesIds: competencyOptions.map((competency) => competency.key).join(',') }
          : {}),
        ...(accreditationBodyIds.length > 0
          ? { accreditationBodyIds: accreditationBodyIds.join(',') }
          : {}),
      }),
      [accreditationBodyIds, competencyOptions]
    );

    const {
      paginatedData: inspectorsData,
      isLoading: inspectorsLoading,
      onLoadMore: onLoadMoreResources,
      onSearchChange: onSearchChangeResources,
    } = useListForFilter<InspectorDTO>({
      endpoint: endpoints.inspectors.base,
      additionalParams: resourceAdditionalParams,
      enabled: !!projectId,
      resetPageOnSearchChange: true,
    });

    const resourceOptions = useMemo(() => {
      const optionsById = new Map<number, { key: number; label: string }>();

      if (currentActivityInspector) {
        optionsById.set(currentActivityInspector.id, {
          key: currentActivityInspector.id,
          label: currentActivityInspector.name,
        });
      }

      inspectorsData.forEach((inspector) => {
        optionsById.set(inspector.id, {
          key: inspector.id,
          label: inspector.name,
        });
      });

      selectedInspectors.forEach((inspector) => {
        optionsById.set(inspector.id, {
          key: inspector.id,
          label: optionsById.get(inspector.id)?.label ?? inspector.name,
        });
      });

      const selectedIdSet = new Set(selectedResourceIds);
      const selectedOptions = selectedInspectors
        .map((inspector) => optionsById.get(inspector.id))
        .filter((option): option is { key: number; label: string } => Boolean(option));
      const unselectedOptions = Array.from(optionsById.values()).filter(
        (option) => !selectedIdSet.has(option.key)
      );

      return [...selectedOptions, ...unselectedOptions];
    }, [currentActivityInspector, inspectorsData, selectedInspectors, selectedResourceIds]);

    const hasSelectedDate = shouldFixAppointmentDate
      ? fixedAppointmentDates.length > 0
      : Boolean(selectedDate);
    const dateLabel =
      shouldFixAppointmentDate && fixedAppointmentDates.length > 0
        ? formatSelectedDateRangeLabel(
            fixedAppointmentDates[0],
            fixedAppointmentDates[fixedAppointmentDates.length - 1]
          )
        : selectedDate
          ? selectedDate.format('DD MMM YY')
          : t('step2.filters.date');

    const isInitialResourcesLoading = inspectorsLoading && resourceOptions.length === 0;
    const isLoadingMoreResources = inspectorsLoading && resourceOptions.length > 0;

    const handleStart = useCallback(() => {
      onStart(selectedInspectors);
    }, [onStart, selectedInspectors]);

    const handleResourceSelectionChange = useCallback(
      (ids: number[]) => {
        if (ids.length === 0) {
          onFiltersChange({ selectedInspectors: [] });
          return;
        }

        const optionsById = new Map(resourceOptions.map((option) => [option.key, option.label]));
        const existingById = new Map(
          selectedInspectors.map((inspector) => [inspector.id, inspector.name])
        );

        onFiltersChange({
          selectedInspectors: ids.map((id) => ({
            id,
            name: optionsById.get(id) ?? existingById.get(id) ?? String(id),
          })),
        });
      },
      [onFiltersChange, resourceOptions, selectedInspectors]
    );

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          mt: 2,
        }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 2,
              py: 1,
              width: '100%',
            }}
          >
            <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>
              {t('createAvailability.title')}
            </Typography>
            {optionsCount != null && optionsCount > 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                {t('createAvailability.optionsCount', { count: optionsCount })}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ px: 2, pb: 1.5, position: 'relative' }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{ rowGap: 1 }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => onFiltersChange({ isPreferred: !isPreferred })}
              sx={filterPillSx({ active: isPreferred })}
            >
              <Typography component="span" sx={{ ...stepHeaderPillLabelSx, color: 'inherit' }}>
                {t('step2.filters.preferred')}
              </Typography>
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                onFiltersChange({
                  inspectorType: inspectorType === 'internal' ? null : 'internal',
                })
              }
              sx={filterPillSx({ active: inspectorType === 'internal' })}
            >
              <Typography component="span" sx={{ ...stepHeaderPillLabelSx, color: 'inherit' }}>
                {t('step2.filters.internal')}
              </Typography>
            </Button>

            <Box sx={{ width: '1px', height: FILTER_PILL_HEIGHT, bgcolor: 'divider', flexShrink: 0 }} />

            <ShuffleFilterAutocompleteMultiple
              label={personFilterLabel}
              options={resourceOptions}
              selectedValues={selectedResourceIds}
              onChange={handleResourceSelectionChange}
              disabled={isInitialResourcesLoading}
              minWidth={220}
              limitTags={1}
              pinSelectedOnTop
              serverSideSearch
              onEndReached={onLoadMoreResources}
              onTextChange={onSearchChangeResources}
              placeholder={
                isInitialResourcesLoading ? t('step2.filters.loadingResources') : undefined
              }
            />

            <ShuffleFilterAutocompleteMultiple
              label={t('step2.filters.competencies')}
              options={competencyOptions}
              selectedValues={selectedCompetencyIds}
              onChange={(selectedCompetencyIds) => onFiltersChange({ selectedCompetencyIds })}
              minWidth={220}
            />

            <ShuffleFilterAutocompleteMultiple
              label={t('step2.filters.businessUnit')}
              options={businessUnitOptionsList}
              selectedValues={selectedBusinessUnitIds}
              onChange={(selectedBusinessUnitIds) => onFiltersChange({ selectedBusinessUnitIds })}
              minWidth={220}
            />

            <Box sx={{ width: '1px', height: FILTER_PILL_HEIGHT, bgcolor: 'divider', flexShrink: 0 }} />

            <Box
              component="button"
              type="button"
              ref={dateButtonRef}
              onClick={
                shouldFixAppointmentDate ? undefined : () => setIsDatePickerOpen(true)
              }
              disabled={shouldFixAppointmentDate}
              sx={filterPillSx({ active: hasSelectedDate, open: isDatePickerOpen })}
            >
              <Typography component="span" sx={{ ...stepHeaderPillLabelSx, color: 'inherit' }}>
                {dateLabel}
              </Typography>
              {!shouldFixAppointmentDate ? (
                <Iconify icon="eva:arrow-ios-downward-fill" width={24} sx={{ color: 'inherit' }} />
              ) : null}
            </Box>

            {showFilterReset ? (
              <Button
                variant="text"
                size="small"
                onClick={onReset}
                sx={{
                  minWidth: 'auto',
                  height: FILTER_PILL_HEIGHT,
                  px: FILTER_PILL_PADDING_X,
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {t('step2.filters.reset')}
              </Button>
            ) : null}

            {showWizardReset && onWizardReset ? (
              <Button
                variant="text"
                size="small"
                onClick={onWizardReset}
                sx={{
                  minWidth: 'auto',
                  height: FILTER_PILL_HEIGHT,
                  px: FILTER_PILL_PADDING_X,
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {t('step2.filters.resetWizard')}
              </Button>
            ) : null}

            <Button
              variant="contained"
              size="small"
              onClick={handleStart}
              endIcon={
                isProgressRunning ? (
                  <Iconify icon="material-symbols:stop-rounded" width={18} />
                ) : (
                  <Iconify icon="eva:arrow-ios-forward-fill" width={18} />
                )
              }
              sx={{
                ml: 'auto',
                height: FILTER_PILL_HEIGHT,
                minWidth: 72,
                px: FILTER_PILL_PADDING_X,
                borderRadius: 999,
                bgcolor: 'grey.900',
                color: 'common.white',
                '&:hover': { bgcolor: 'grey.800' },
              }}
            >
              {isProgressRunning ? t('step2.filters.stop') : t('step2.filters.start')}
            </Button>
          </Stack>

          {!shouldFixAppointmentDate ? (
            <Box
              sx={{
                position: 'absolute',
                width: 0,
                height: 0,
                overflow: 'visible',
                pointerEvents: 'none',
              }}
            >
              <RestrictedDatePicker
                dueDate={currentActivity.dueDate ? dayjs(currentActivity.dueDate) : undefined}
                label={t('step2.filters.date')}
                value={selectedDate}
                onChange={(newValue) => {
                  onFiltersChange({ selectedDate: newValue });
                  setIsDatePickerOpen(false);
                }}
                open={isDatePickerOpen}
                onClose={() => setIsDatePickerOpen(false)}
                slots={{
                  field: () => null,
                }}
                slotProps={{
                  popper: {
                    anchorEl: dateButtonRef.current,
                    placement: 'bottom-start',
                    sx: { pointerEvents: 'auto' },
                  },
                }}
              />
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }
);

Step2Filters.displayName = 'Step2Filters';
